"use client";

import dictionary from '@/utils/dictionary';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { HTMLAttributeAnchorTarget, MouseEvent, SVGProps, Suspense, useEffect, useState } from 'react';
import { JSX } from 'react/jsx-runtime';
import Spinner from '../spinner/Spinner';
import styles from './button.module.scss';

// Type definitions corresponding to the original enums
export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonStyle = 'filled' | 'bezeled' | 'bezeled_gray' | 'transparent';
export type ButtonStatus = 'enabled' | 'loading' | 'success' | 'disabled';
export type ButtonLayout = 'horizontal' | 'vertical';

// Props interface for the Button component
export interface ButtonProps {
    label?: string;
    href?: string;
    hrefTarget?: HTMLAttributeAnchorTarget;
    rel?: string;
    loadingLabel?: string;
    successLabel?: string;
    startIcon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
    endIcon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
    iconClassName?: string;
    iconSize?: number;
    onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void | Promise<void>;
    size?: ButtonSize;
    style?: ButtonStyle;
    status?: ButtonStatus;
    layout?: ButtonLayout;
    type?: "submit" | "reset" | "button";
    className?: string;
    color?: string;
    ariaLabel?: string;
    title?: string;
    ariaPressed?: boolean;
}

function ButtonImpl({
    label,
    href,
    hrefTarget,
    rel,
    loadingLabel = dictionary.global.loading,
    successLabel = dictionary.global.success,
    startIcon,
    endIcon,
    iconClassName,
    iconSize = 15,
    onClick,
    size = 'medium',
    style = 'filled',
    status = 'enabled',
    layout = 'horizontal',
    type,
    className,
    color,
    ariaLabel,
    title,
    ariaPressed,
    pathname,
    searchParamsKey,
}: ButtonProps & { pathname?: string; searchParamsKey?: string }) {
    const [isPressed, setIsPressed] = useState(false);
    const [hovering, setHovering] = useState(false);
    const currentStyle = style === 'bezeled' && hovering ? 'filled' : style;
    const routeKey = `${pathname}?${searchParamsKey}`;
    const [navigationOrigin, setNavigationOrigin] = useState<string | null>(null);
    const isNavigating = navigationOrigin === routeKey;

    useEffect(() => {
        const handleHashChange = () => setNavigationOrigin(null);
        window.addEventListener('hashchange', handleHashChange);
        window.addEventListener('popstate', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            window.removeEventListener('popstate', handleHashChange);
        };
    }, []);

    const handleOnClick = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        if (!isInteractive) {
            e.preventDefault();
            return;
        }

        setIsPressed(true);

        const isModifiedClick =
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey ||
            // 0 = primary/left click
            e.button !== 0;

        // Only show a navigation loading state for normal in-tab navigation.
        // If the user opens in a new tab/window (cmd/ctrl/middle-click), don't.
        if (href && hrefTarget !== '_blank' && !isModifiedClick) {
            try {
                const destination = new URL(href, window.location.origin);
                const isInternal = destination.origin === window.location.origin;
                const isSamePathAndSearch =
                    destination.pathname === window.location.pathname &&
                    destination.search === window.location.search;
                const isHashOnlyNavigation = isSamePathAndSearch && destination.hash.length > 0;

                if (isInternal && !isHashOnlyNavigation) {
                    setNavigationOrigin(routeKey);
                }
            } catch {
                // If parsing fails (rare), avoid getting stuck in loading.
                setNavigationOrigin(null);
            }
        }

        onClick?.(e);

        // The delay mimics the original Compose animation for the pressed state
        setTimeout(() => {
            setIsPressed(false);
        }, 150);
    };

    const isInteractive = (status === 'enabled' && (onClick || href)) || type == 'submit';

    // Use local navigation loading state when href is present
    const effectiveStatus = isNavigating ? 'loading' : status;

    const currentLabel =
        effectiveStatus === 'loading' ? loadingLabel :
            effectiveStatus === 'success' ? successLabel :
                label;

    const StartIcon = startIcon
    const EndIcon = endIcon

    const commonProps = {
        className: clsx(
            styles.button,
            styles[`size_${size}`],
            styles[`style_${currentStyle}`],
            styles[`status_${effectiveStatus}`],
            styles[`layout_${layout}`],
            { [styles.pressed]: isPressed },
            className
        ),
        onClick: handleOnClick,
        onMouseEnter: () => setHovering(true),
        onMouseLeave: () => setHovering(false),
        style: { '--button-color': color } as React.CSSProperties,
        'aria-label': ariaLabel,
        'aria-busy': effectiveStatus === 'loading',
        title,
    };

    const content = (
        <>
            {effectiveStatus === 'loading' ? <Spinner containerClassName={styles.spinner} aria-label={dictionary.global.loading} /> : null}
            <span className={styles.content}>
                {StartIcon && effectiveStatus !== 'loading' ? <StartIcon className={clsx(styles.icon, iconClassName)} width={iconSize} /> : null}
                {currentLabel !== undefined ? <span className={styles.label}>{currentLabel}</span> : null}
                {EndIcon && effectiveStatus !== 'loading' ? <EndIcon className={clsx(styles.icon, iconClassName)} width={iconSize} /> : null}
            </span>
        </>
    );

    if (href) {
        return (
            <Link {...commonProps} href={href} target={hrefTarget} rel={rel} aria-disabled={effectiveStatus === 'disabled'}>
                {content}
            </Link>
        );
    }
    return <button {...commonProps} disabled={effectiveStatus === 'disabled'} type={type ?? 'button'} aria-pressed={ariaPressed}>{content}</button>;

}

function ButtonWithRouter(props: ButtonProps) {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    return (
        <ButtonImpl
            {...props}
            pathname={pathname}
            searchParamsKey={searchParams.toString()}
        />
    );
}

export default function Button(props: ButtonProps) {
    return (
        <Suspense fallback={<ButtonImpl {...props} />}>
            <ButtonWithRouter {...props} />
        </Suspense>
    );
}
